import type {
  IDBPDatabase,
  IDBPTransaction,
} from 'idb';

import type {
  FenixBackupV1,
  LogicalBackupSnapshot,
} from '../domain/backup.ts';

import {
  isTerminalRestoreStatus,
  validRestoreOperation,
  validRestorePreviewToken,
} from '../domain/restore.ts';

import type {
  RestoreApplyOutcome,
  RestoreFailureCode,
  RestoreOperation,
  RestorePreviewToken,
  RestoreRecoveryOutcome,
  RestoreResult,
} from '../domain/restore.ts';

import {
  validateRestoreSnapshot,
} from '../domain/restore-backup.ts';

import type {
  RestoreRepository,
  StageRestoreInput,
} from '../ports/restore-repository.ts';

import {
  openFenixDatabase,
  validDatasetControl,
} from './fenix-db.ts';

import type {
  DatasetControl,
  FenixSchema,
} from './fenix-db.ts';

import {
  canonical,
} from './canonical.ts';

export const RESTORE_DATASET_STORES = [
  'heads',
  'history',
  'receipts',

  'training_exercises',
  'training_exercise_revisions',
  'training_routines',
  'training_routine_revisions',
  'training_planned_sessions',
  'training_planned_session_history',
  'training_executions',
  'training_working_sets',
  'training_receipts',

  'nutrition_recipes',
  'nutrition_recipe_revisions',
  'nutrition_planned_meals',
  'nutrition_planned_meal_history',
  'nutrition_consumptions',
  'nutrition_consumption_history',
  'nutrition_shopping_items',
  'nutrition_shopping_item_history',
  'nutrition_receipts',

  'progress_weights',
  'progress_weight_history',
  'progress_receipts',
] as const;

const RESTORE_TX_STORES = [
  'control',
  'restore_operations',
  ...RESTORE_DATASET_STORES,
] as const;

type RestoreTx<
  M extends 'readonly' | 'readwrite',
> =
  IDBPTransaction<
    FenixSchema,
    typeof RESTORE_TX_STORES,
    M
  >;

export type RestoreFault =
  | 'quota-stage'
  | 'after-fence'
  | 'after-switch'
  | 'before-finalize'
  | 'after-rollback-switch'
  | 'cleanup-failure';

export class RestoreLabFaults {
  next: RestoreFault | null =
    null;

  hit(
    point: RestoreFault,
  ) {
    if (this.next !== point) {
      return false;
    }

    this.next = null;

    return true;
  }
}

function failure<T = never>(
  code: RestoreFailureCode,
): RestoreResult<T> {
  return {
    ok: false,
    code,
  };
}

function storageCode(
  error: unknown,
): RestoreFailureCode {
  const name =
    error instanceof Error
      ? error.name
      : '';

  switch (name) {
    case 'QuotaExceededError':
      return 'STORAGE_QUOTA';

    case 'AbortError':
      return 'TRANSACTION_ABORTED';

    case 'ConstraintError':
    case 'DataError':
      return 'RESTORE_CORRUPTION';

    default:
      return 'STORAGE_UNAVAILABLE';
  }
}

function openCode(
  code: string,
): RestoreFailureCode {
  switch (code) {
    case 'TRANSACTION_ABORTED':
      return 'TRANSACTION_ABORTED';

    case 'STORAGE_QUOTA':
      return 'STORAGE_QUOTA';

    case 'CORRUPTION_DETECTED':
      return 'RESTORE_CORRUPTION';

    default:
      return 'STORAGE_UNAVAILABLE';
  }
}

function scope(
  datasetId: string,
) {
  return IDBKeyRange.bound(
    [datasetId],
    [datasetId, []],
  );
}

function samePreview(
  left: RestorePreviewToken,
  right: RestorePreviewToken,
) {
  return (
    canonical(left)
      === canonical(right)
  );
}

function previewMatchesControl(
  preview: RestorePreviewToken,
  control: DatasetControl,
) {
  return (
    preview.activeDatasetId
      === control.activeDatasetId
    && preview.datasetRevision
      === control.datasetRevision
    && preview.controlEpoch
      === control.controlEpoch
    && preview.schemaVersion
      === control.schemaVersion
  );
}

function bag(
  values: readonly unknown[],
) {
  return values
    .map(canonical)
    .sort();
}

function normalized(
  snapshot: LogicalBackupSnapshot,
) {
  return {
    schemaVersion:
      snapshot.schemaVersion,

    dataset: {
      training: {
        exercises:
          bag(
            snapshot.dataset
              .training.exercises,
          ),

        routines:
          bag(
            snapshot.dataset
              .training.routines,
          ),

        plannedSessions:
          bag(
            snapshot.dataset
              .training.plannedSessions,
          ),

        executions:
          bag(
            snapshot.dataset
              .training.executions,
          ),

        workingSets:
          bag(
            snapshot.dataset
              .training.workingSets,
          ),
      },

      nutrition: {
        recipes:
          bag(
            snapshot.dataset
              .nutrition.recipes,
          ),

        plannedMeals:
          bag(
            snapshot.dataset
              .nutrition.plannedMeals,
          ),

        consumptions:
          bag(
            snapshot.dataset
              .nutrition.consumptions,
          ),

        shoppingItems:
          bag(
            snapshot.dataset
              .nutrition.shoppingItems,
          ),
      },

      progress: {
        weights:
          bag(
            snapshot.dataset
              .progress.weights,
          ),
      },

      dailyRoutine: {
        checks:
          bag(
            snapshot.dataset
              .dailyRoutine.checks,
          ),
      },

      settings: {},
    },

    revisionHistory: {
      dailyRoutine: {
        checks:
          bag(
            snapshot.revisionHistory
              .dailyRoutine.checks,
          ),
      },

      training: {
        exercises:
          bag(
            snapshot.revisionHistory
              .training.exercises,
          ),

        routines:
          bag(
            snapshot.revisionHistory
              .training.routines,
          ),

        plannedSessions:
          bag(
            snapshot.revisionHistory
              .training.plannedSessions,
          ),
      },

      nutrition: {
        recipes:
          bag(
            snapshot.revisionHistory
              .nutrition.recipes,
          ),

        plannedMeals:
          bag(
            snapshot.revisionHistory
              .nutrition.plannedMeals,
          ),

        consumptions:
          bag(
            snapshot.revisionHistory
              .nutrition.consumptions,
          ),

        shoppingItems:
          bag(
            snapshot.revisionHistory
              .nutrition.shoppingItems,
          ),
      },

      progress: {
        weights:
          bag(
            snapshot.revisionHistory
              .progress.weights,
          ),
      },
    },

    mediaManifest: {
      entries: [],
    },
  };
}

export class IndexedRestoreRepository
implements RestoreRepository {
  private readonly db:
    IDBPDatabase<FenixSchema>;

  private readonly faults:
    RestoreLabFaults;

  constructor(
    db: IDBPDatabase<FenixSchema>,
    faults = new RestoreLabFaults(),
  ) {
    this.db =
      db;

    this.faults =
      faults;
  }

  close() {
    this.db.close();
  }

  private async transaction<
    M extends 'readonly' | 'readwrite',
    T,
  >(
    mode: M,
    run: (
      tx: RestoreTx<M>,
    ) => Promise<RestoreResult<T>>,
  ): Promise<RestoreResult<T>> {
    let tx:
      RestoreTx<M>
      | undefined;

    try {
      tx =
        this.db.transaction(
          RESTORE_TX_STORES,
          mode,
          {
            durability: 'strict',
          },
        );

      void tx.done.catch(
        () => {},
      );

      const result =
        await run(tx);

      if (!result.ok) {
        try {
          tx.abort();
        }
        catch {
          // Transaction may already be settled.
        }

        await tx.done.catch(
          () => {},
        );

        return result;
      }

      await tx.done;

      return result;
    }
    catch (error) {
      if (tx) {
        try {
          tx.abort();
        }
        catch {
          // Transaction may already be settled.
        }

        await tx.done.catch(
          () => {},
        );
      }

      return failure(
        storageCode(error),
      );
    }
  }

  private async deleteDataset<
    M extends 'readwrite',
  >(
    tx: RestoreTx<M>,
    datasetId: string,
  ) {
    const range =
      scope(datasetId);

    for (
      const name
      of RESTORE_DATASET_STORES
    ) {
      await tx
        .objectStore(name)
        .delete(range);
    }
  }

  private async countDataset<
    M extends 'readonly' | 'readwrite',
  >(
    tx: RestoreTx<M>,
    datasetId: string,
  ) {
    let total = 0;

    const range =
      scope(datasetId);

    for (
      const name
      of RESTORE_DATASET_STORES
    ) {
      total +=
        await tx
          .objectStore(name)
          .count(range);
    }

    return total;
  }

  private async readSnapshot<
    M extends 'readonly' | 'readwrite',
  >(
    tx: RestoreTx<M>,
    datasetId: string,
  ): Promise<LogicalBackupSnapshot> {
    const range =
      scope(datasetId);

    const [
      heads,
      dailyHistory,

      exercises,
      exerciseRevisions,
      routines,
      routineRevisions,
      sessions,
      sessionHistory,
      executions,
      workingSets,

      recipes,
      recipeRevisions,
      meals,
      mealHistory,
      consumptions,
      consumptionHistory,
      shopping,
      shoppingHistory,

      weights,
      weightHistory,
    ] =
      await Promise.all([
        tx.objectStore('heads')
          .getAll(range),

        tx.objectStore('history')
          .getAll(range),

        tx.objectStore('training_exercises')
          .getAll(range),

        tx.objectStore('training_exercise_revisions')
          .getAll(range),

        tx.objectStore('training_routines')
          .getAll(range),

        tx.objectStore('training_routine_revisions')
          .getAll(range),

        tx.objectStore('training_planned_sessions')
          .getAll(range),

        tx.objectStore('training_planned_session_history')
          .getAll(range),

        tx.objectStore('training_executions')
          .getAll(range),

        tx.objectStore('training_working_sets')
          .getAll(range),

        tx.objectStore('nutrition_recipes')
          .getAll(range),

        tx.objectStore('nutrition_recipe_revisions')
          .getAll(range),

        tx.objectStore('nutrition_planned_meals')
          .getAll(range),

        tx.objectStore('nutrition_planned_meal_history')
          .getAll(range),

        tx.objectStore('nutrition_consumptions')
          .getAll(range),

        tx.objectStore('nutrition_consumption_history')
          .getAll(range),

        tx.objectStore('nutrition_shopping_items')
          .getAll(range),

        tx.objectStore('nutrition_shopping_item_history')
          .getAll(range),

        tx.objectStore('progress_weights')
          .getAll(range),

        tx.objectStore('progress_weight_history')
          .getAll(range),
      ] as const);

    return {
      schemaVersion: 1,

      dataset: {
        training: {
          exercises:
            exercises.map(
              row => row.record,
            ),

          routines:
            routines.map(
              row => row.record,
            ),

          plannedSessions:
            sessions.map(
              row => row.record,
            ),

          executions:
            executions.map(
              row => row.record,
            ),

          workingSets:
            workingSets.map(
              row => ({
                position:
                  row.position,

                workingSet:
                  row.record,
              }),
            ),
        },

        nutrition: {
          recipes:
            recipes.map(
              row => row.record,
            ),

          plannedMeals:
            meals.map(
              row => row.record,
            ),

          consumptions:
            consumptions.map(
              row => row.record,
            ),

          shoppingItems:
            shopping.map(
              row => row.record,
            ),
        },

        progress: {
          weights:
            weights.map(
              row => row.record,
            ),
        },

        dailyRoutine: {
          checks:
            heads.map(
              row => row.check,
            ),
        },

        settings: {},
      },

      revisionHistory: {
        dailyRoutine: {
          checks:
            dailyHistory.map(
              row => row.check,
            ),
        },

        training: {
          exercises:
            exerciseRevisions.map(
              row => row.record,
            ),

          routines:
            routineRevisions.map(
              row => row.record,
            ),

          plannedSessions:
            sessionHistory.map(
              row => row.record,
            ),
        },

        nutrition: {
          recipes:
            recipeRevisions.map(
              row => row.record,
            ),

          plannedMeals:
            mealHistory.map(
              row => row.record,
            ),

          consumptions:
            consumptionHistory.map(
              row => row.record,
            ),

          shoppingItems:
            shoppingHistory.map(
              row => row.record,
            ),
        },

        progress: {
          weights:
            weightHistory.map(
              row => row.record,
            ),
        },
      },

      mediaManifest: {
        entries: [],
      },
    };
  }

  private async writeCandidate(
    tx: RestoreTx<'readwrite'>,
    datasetId: string,
    backup: FenixBackupV1,
  ) {
    for (
      const check
      of backup.dataset
        .dailyRoutine.checks
    ) {
      await tx
        .objectStore('heads')
        .add(
          {
            datasetId,
            check,
          },
          [
            datasetId,
            check.checkId,
          ],
        );
    }

    for (
      const check
      of backup.revisionHistory
        .dailyRoutine.checks
    ) {
      await tx
        .objectStore('history')
        .add(
          {
            datasetId,
            check,
          },
          [
            datasetId,
            check.checkId,
            check.revision,
          ],
        );
    }

    for (
      const record
      of backup.dataset
        .training.exercises
    ) {
      await tx
        .objectStore(
          'training_exercises',
        )
        .add(
          {
            datasetId,
            record,
          },
          [
            datasetId,
            record.exerciseId,
          ],
        );
    }

    for (
      const record
      of backup.revisionHistory
        .training.exercises
    ) {
      await tx
        .objectStore(
          'training_exercise_revisions',
        )
        .add(
          {
            datasetId,
            record,
          },
          [
            datasetId,
            record.exerciseRevisionId,
          ],
        );
    }

    for (
      const record
      of backup.dataset
        .training.routines
    ) {
      await tx
        .objectStore(
          'training_routines',
        )
        .add(
          {
            datasetId,
            record,
          },
          [
            datasetId,
            record.routineId,
          ],
        );
    }

    for (
      const record
      of backup.revisionHistory
        .training.routines
    ) {
      await tx
        .objectStore(
          'training_routine_revisions',
        )
        .add(
          {
            datasetId,
            record,
          },
          [
            datasetId,
            record.routineRevisionId,
          ],
        );
    }

    for (
      const record
      of backup.dataset
        .training.plannedSessions
    ) {
      await tx
        .objectStore(
          'training_planned_sessions',
        )
        .add(
          {
            datasetId,
            record,
          },
          [
            datasetId,
            record.sessionId,
          ],
        );
    }

    for (
      const record
      of backup.revisionHistory
        .training.plannedSessions
    ) {
      await tx
        .objectStore(
          'training_planned_session_history',
        )
        .add(
          {
            datasetId,
            record,
          },
          [
            datasetId,
            record.sessionId,
            record.revision,
          ],
        );
    }

    for (
      const record
      of backup.dataset
        .training.executions
    ) {
      await tx
        .objectStore(
          'training_executions',
        )
        .add(
          {
            datasetId,
            record,
          },
          [
            datasetId,
            record.executionId,
          ],
        );
    }

    for (
      const item
      of backup.dataset
        .training.workingSets
    ) {
      await tx
        .objectStore(
          'training_working_sets',
        )
        .add(
          {
            datasetId,
            record:
              item.workingSet,
            position:
              item.position,
          },
          [
            datasetId,
            item.workingSet
              .workingSetId,
          ],
        );
    }

    for (
      const record
      of backup.dataset
        .nutrition.recipes
    ) {
      await tx
        .objectStore(
          'nutrition_recipes',
        )
        .add(
          {
            datasetId,
            record,
          },
          [
            datasetId,
            record.recipeId,
          ],
        );
    }

    for (
      const record
      of backup.revisionHistory
        .nutrition.recipes
    ) {
      await tx
        .objectStore(
          'nutrition_recipe_revisions',
        )
        .add(
          {
            datasetId,
            record,
          },
          [
            datasetId,
            record.recipeRevisionId,
          ],
        );
    }

    for (
      const record
      of backup.dataset
        .nutrition.plannedMeals
    ) {
      await tx
        .objectStore(
          'nutrition_planned_meals',
        )
        .add(
          {
            datasetId,
            record,
          },
          [
            datasetId,
            record.plannedMealId,
          ],
        );
    }

    for (
      const record
      of backup.revisionHistory
        .nutrition.plannedMeals
    ) {
      await tx
        .objectStore(
          'nutrition_planned_meal_history',
        )
        .add(
          {
            datasetId,
            record,
          },
          [
            datasetId,
            record.plannedMealId,
            record.revision,
          ],
        );
    }

    for (
      const record
      of backup.dataset
        .nutrition.consumptions
    ) {
      await tx
        .objectStore(
          'nutrition_consumptions',
        )
        .add(
          {
            datasetId,
            record,
          },
          [
            datasetId,
            record.consumptionId,
          ],
        );
    }

    for (
      const record
      of backup.revisionHistory
        .nutrition.consumptions
    ) {
      await tx
        .objectStore(
          'nutrition_consumption_history',
        )
        .add(
          {
            datasetId,
            record,
          },
          [
            datasetId,
            record.consumptionId,
            record.revision,
          ],
        );
    }

    for (
      const record
      of backup.dataset
        .nutrition.shoppingItems
    ) {
      await tx
        .objectStore(
          'nutrition_shopping_items',
        )
        .add(
          {
            datasetId,
            record,
          },
          [
            datasetId,
            record.shoppingItemId,
          ],
        );
    }

    for (
      const record
      of backup.revisionHistory
        .nutrition.shoppingItems
    ) {
      await tx
        .objectStore(
          'nutrition_shopping_item_history',
        )
        .add(
          {
            datasetId,
            record,
          },
          [
            datasetId,
            record.shoppingItemId,
            record.revision,
          ],
        );
    }

    for (
      const record
      of backup.dataset
        .progress.weights
    ) {
      await tx
        .objectStore(
          'progress_weights',
        )
        .add(
          {
            datasetId,
            record,
          },
          [
            datasetId,
            record.observationId,
          ],
        );
    }

    for (
      const record
      of backup.revisionHistory
        .progress.weights
    ) {
      await tx
        .objectStore(
          'progress_weight_history',
        )
        .add(
          {
            datasetId,
            record,
          },
          [
            datasetId,
            record.observationId,
            record.revision,
          ],
        );
    }
  }

  private async verifyDataset(
    datasetId: string,
    operation: RestoreOperation,
  ): Promise<RestoreResult<boolean>> {
    return this.transaction(
      'readonly',
      async tx => {
        const snapshot =
          await this.readSnapshot(
            tx,
            datasetId,
          );

        const validated =
          validateRestoreSnapshot(
            snapshot,
            operation.backupId,
            operation.createdAt,
          );

        return {
          ok: true,
          value:
            validated.ok,
        };
      },
    );
  }

  async stage(
    input: StageRestoreInput,
  ): Promise<
    RestoreResult<RestorePreviewToken>
  > {
    if (
      !validRestoreOperation({
        restoreOperationId:
          input.restoreOperationId,

        backupId:
          input.backup.backupId,

        stagingDatasetId:
          input.stagingDatasetId,

        status: 'BUILDING',

        createdAt:
          input.now,

        updatedAt:
          input.now,
      })
    ) {
      return failure(
        'RESTORE_INVALID_BACKUP',
      );
    }

    return this.transaction(
      'readwrite',
      async tx => {
        const control =
          await tx
            .objectStore('control')
            .get('control');

        if (
          !validDatasetControl(
            control,
          )
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        if (
          control.restoreFence
            !== null
        ) {
          return failure(
            'RESTORE_IN_PROGRESS',
          );
        }

        if (
          control.recoveryDatasetId
            !== null
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        if (
          input.stagingDatasetId
            === control.activeDatasetId
        ) {
          return failure(
            'RESTORE_OPERATION_CONFLICT',
          );
        }

        const operations =
          await tx
            .objectStore(
              'restore_operations',
            )
            .getAll();

        if (
          operations.some(
            operation =>
              !validRestoreOperation(
                operation,
              ),
          )
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        for (
          const operation
          of operations
        ) {
          if (
            isTerminalRestoreStatus(
              operation.status,
            )
          ) {
            continue;
          }

          if (
            (
              operation.status
                === 'VALIDATED'
              || operation.status
                === 'BUILDING'
            )
            && operation.stagingDatasetId
              !== control.activeDatasetId
            && operation.stagingDatasetId
              !== control.recoveryDatasetId
          ) {
            const stale =
              operation.preview
                === undefined
              || !previewMatchesControl(
                operation.preview,
                control,
              );

            if (stale) {
              await this.deleteDataset(
                tx,
                operation.stagingDatasetId,
              );

              const abandoned:
                RestoreOperation = {
                  ...operation,

                  status:
                    'ABANDONED',

                  updatedAt:
                    input.now,

                  abandonedAt:
                    input.now,

                  retiredCleanupCandidate: {
                    datasetId:
                      operation.stagingDatasetId,

                    reason:
                      'ABANDONED_STAGING',

                    retiredAt:
                      input.now,
                  },
                };

              await tx
                .objectStore(
                  'restore_operations',
                )
                .put(
                  abandoned,
                  operation
                    .restoreOperationId,
                );

              continue;
            }
          }

          return failure(
            'RESTORE_IN_PROGRESS',
          );
        }

        if (
          await tx
            .objectStore(
              'restore_operations',
            )
            .get(
              input.restoreOperationId,
            )
        ) {
          return failure(
            'RESTORE_OPERATION_CONFLICT',
          );
        }

        if (
          await this.countDataset(
            tx,
            input.stagingDatasetId,
          ) !== 0
        ) {
          return failure(
            'RESTORE_OPERATION_CONFLICT',
          );
        }

        const preview:
          RestorePreviewToken = {
            activeDatasetId:
              control.activeDatasetId,

            datasetRevision:
              control.datasetRevision,

            controlEpoch:
              control.controlEpoch,

            schemaVersion:
              control.schemaVersion,

            restoreOperationId:
              input.restoreOperationId,

            stagingDatasetId:
              input.stagingDatasetId,
          };

        const building:
          RestoreOperation = {
            restoreOperationId:
              input.restoreOperationId,

            backupId:
              input.backup.backupId,

            stagingDatasetId:
              input.stagingDatasetId,

            sourceDatasetId:
              control.activeDatasetId,

            status:
              'BUILDING',

            createdAt:
              input.now,

            updatedAt:
              input.now,
          };

        await tx
          .objectStore(
            'restore_operations',
          )
          .add(
            building,
            building.restoreOperationId,
          );

        await this.writeCandidate(
          tx,
          input.stagingDatasetId,
          input.backup,
        );

        if (
          this.faults.hit(
            'quota-stage',
          )
        ) {
          throw new DOMException(
            'Synthetic Restore quota',
            'QuotaExceededError',
          );
        }

        const snapshot =
          await this.readSnapshot(
            tx,
            input.stagingDatasetId,
          );

        if (
          canonical(
            normalized(snapshot),
          )
          !== canonical(
            normalized(
              input.backup,
            ),
          )
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        const validated =
          validateRestoreSnapshot(
            snapshot,
            input.backup.backupId,
            input.backup.exportedAt,
          );

        if (!validated.ok) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        const operation:
          RestoreOperation = {
            ...building,

            status:
              'VALIDATED',

            updatedAt:
              input.now,

            preview,
          };

        await tx
          .objectStore(
            'restore_operations',
          )
          .put(
            operation,
            operation.restoreOperationId,
          );

        return {
          ok: true,
          value: preview,
        };
      },
    );
  }

  private async state(
    operationId: string,
  ): Promise<
    RestoreResult<{
      control: DatasetControl;
      operation: RestoreOperation;
    }>
  > {
    return this.transaction(
      'readonly',
      async tx => {
        const control =
          await tx
            .objectStore('control')
            .get('control');

        const operation =
          await tx
            .objectStore(
              'restore_operations',
            )
            .get(operationId);

        if (
          !validDatasetControl(
            control,
          )
          || !validRestoreOperation(
            operation,
          )
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        return {
          ok: true,
          value: {
            control,
            operation,
          },
        };
      },
    );
  }

  private async acquireFence(
    preview: RestorePreviewToken,
    now: string,
  ): Promise<RestoreResult<void>> {
    return this.transaction(
      'readwrite',
      async tx => {
        const control =
          await tx
            .objectStore('control')
            .get('control');

        const operation =
          await tx
            .objectStore(
              'restore_operations',
            )
            .get(
              preview.restoreOperationId,
            );

        if (
          !validDatasetControl(
            control,
          )
          || !validRestoreOperation(
            operation,
          )
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        if (
          operation.status
            !== 'VALIDATED'
          || operation.preview
            === undefined
          || !samePreview(
            operation.preview,
            preview,
          )
        ) {
          return failure(
            'RESTORE_OPERATION_CONFLICT',
          );
        }

        if (
          control.restoreFence
            !== null
        ) {
          return failure(
            'RESTORE_IN_PROGRESS',
          );
        }

        if (
          control.recoveryDatasetId
            !== null
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        if (
          !previewMatchesControl(
            preview,
            control,
          )
        ) {
          return failure(
            'RESTORE_PREVIEW_STALE',
          );
        }

        if (
          !Number.isSafeInteger(
            control.controlEpoch + 1,
          )
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        await tx
          .objectStore('control')
          .put({
            ...control,

            controlEpoch:
              control.controlEpoch + 1,

            restoreFence: {
              operationId:
                preview
                  .restoreOperationId,
            },
          });

        await tx
          .objectStore(
            'restore_operations',
          )
          .put(
            {
              ...operation,

              status:
                'APPLYING',

              updatedAt:
                now,

              sourceDatasetId:
                control.activeDatasetId,
            },
            operation.restoreOperationId,
          );

        return {
          ok: true,
          value: undefined,
        };
      },
    );
  }

  private async forwardSwitch(
    operationId: string,
    now: string,
  ): Promise<RestoreResult<void>> {
    return this.transaction(
      'readwrite',
      async tx => {
        const control =
          await tx
            .objectStore('control')
            .get('control');

        const operation =
          await tx
            .objectStore(
              'restore_operations',
            )
            .get(operationId);

        if (
          !validDatasetControl(
            control,
          )
          || !validRestoreOperation(
            operation,
          )
          || operation.status
            !== 'APPLYING'
          || operation.preview
            === undefined
          || operation.sourceDatasetId
            === undefined
          || control.restoreFence
            ?.operationId
            !== operationId
          || control.recoveryDatasetId
            !== null
          || control.activeDatasetId
            !== operation.sourceDatasetId
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        if (
          !Number.isSafeInteger(
            control.controlEpoch + 1,
          )
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        await tx
          .objectStore('control')
          .put({
            ...control,

            activeDatasetId:
              operation.stagingDatasetId,

            recoveryDatasetId:
              operation.sourceDatasetId,

            controlEpoch:
              control.controlEpoch + 1,

            datasetRevision: 0,
          });

        await tx
          .objectStore(
            'restore_operations',
          )
          .put(
            {
              ...operation,

              status:
                'SWITCHING',

              updatedAt:
                now,
            },
            operation.restoreOperationId,
          );

        return {
          ok: true,
          value: undefined,
        };
      },
    );
  }

  private async markVerifying(
    operationId: string,
    now: string,
  ): Promise<RestoreResult<void>> {
    return this.transaction(
      'readwrite',
      async tx => {
        const control =
          await tx
            .objectStore('control')
            .get('control');

        const operation =
          await tx
            .objectStore(
              'restore_operations',
            )
            .get(operationId);

        if (
          !validDatasetControl(
            control,
          )
          || !validRestoreOperation(
            operation,
          )
          || operation.status
            !== 'SWITCHING'
          || operation.sourceDatasetId
            === undefined
          || control.restoreFence
            ?.operationId
            !== operationId
          || control.activeDatasetId
            !== operation.stagingDatasetId
          || control.recoveryDatasetId
            !== operation.sourceDatasetId
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        await tx
          .objectStore(
            'restore_operations',
          )
          .put(
            {
              ...operation,

              status:
                'VERIFYING',

              updatedAt:
                now,
            },
            operation.restoreOperationId,
          );

        return {
          ok: true,
          value: undefined,
        };
      },
    );
  }

  private async markRollingBack(
    operationId: string,
    now: string,
  ): Promise<RestoreResult<void>> {
    return this.transaction(
      'readwrite',
      async tx => {
        const control =
          await tx
            .objectStore('control')
            .get('control');

        const operation =
          await tx
            .objectStore(
              'restore_operations',
            )
            .get(operationId);

        if (
          !validDatasetControl(
            control,
          )
          || !validRestoreOperation(
            operation,
          )
          || operation.status
            !== 'VERIFYING'
          || operation.sourceDatasetId
            === undefined
          || control.restoreFence
            ?.operationId
            !== operationId
          || control.activeDatasetId
            !== operation.stagingDatasetId
          || control.recoveryDatasetId
            !== operation.sourceDatasetId
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        await tx
          .objectStore(
            'restore_operations',
          )
          .put(
            {
              ...operation,

              status:
                'ROLLING_BACK',

              updatedAt:
                now,
            },
            operation.restoreOperationId,
          );

        return {
          ok: true,
          value: undefined,
        };
      },
    );
  }

  private async switchBack(
    operationId: string,
    now: string,
  ): Promise<RestoreResult<void>> {
    return this.transaction(
      'readwrite',
      async tx => {
        const control =
          await tx
            .objectStore('control')
            .get('control');

        const operation =
          await tx
            .objectStore(
              'restore_operations',
            )
            .get(operationId);

        if (
          !validDatasetControl(
            control,
          )
          || !validRestoreOperation(
            operation,
          )
          || operation.status
            !== 'ROLLING_BACK'
          || operation.preview
            === undefined
          || operation.sourceDatasetId
            === undefined
          || control.restoreFence
            ?.operationId
            !== operationId
          || control.activeDatasetId
            !== operation.stagingDatasetId
          || control.recoveryDatasetId
            !== operation.sourceDatasetId
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        if (
          !Number.isSafeInteger(
            control.controlEpoch + 1,
          )
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        await tx
          .objectStore('control')
          .put({
            ...control,

            activeDatasetId:
              operation.sourceDatasetId,

            recoveryDatasetId:
              operation.stagingDatasetId,

            controlEpoch:
              control.controlEpoch + 1,

            datasetRevision:
              operation.preview
                .datasetRevision,
          });

        await tx
          .objectStore(
            'restore_operations',
          )
          .put(
            {
              ...operation,

              status:
                'VERIFYING_ROLLBACK',

              updatedAt:
                now,
            },
            operation.restoreOperationId,
          );

        return {
          ok: true,
          value: undefined,
        };
      },
    );
  }

  private async finalizeSuccess(
    operationId: string,
    now: string,
  ): Promise<
    RestoreResult<RestoreApplyOutcome>
  > {
    return this.transaction(
      'readwrite',
      async tx => {
        const control =
          await tx
            .objectStore('control')
            .get('control');

        const operation =
          await tx
            .objectStore(
              'restore_operations',
            )
            .get(operationId);

        if (
          !validDatasetControl(
            control,
          )
          || !validRestoreOperation(
            operation,
          )
          || operation.status
            !== 'VERIFYING'
          || operation.sourceDatasetId
            === undefined
          || control.restoreFence
            ?.operationId
            !== operationId
          || control.activeDatasetId
            !== operation.stagingDatasetId
          || control.recoveryDatasetId
            !== operation.sourceDatasetId
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        if (
          !Number.isSafeInteger(
            control.controlEpoch + 1,
          )
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        const completed:
          RestoreOperation = {
            ...operation,

            status:
              'COMPLETED',

            updatedAt:
              now,

            completedAt:
              now,

            successDatasetId:
              operation.stagingDatasetId,

            retiredCleanupCandidate: {
              datasetId:
                operation.sourceDatasetId,

              reason:
                'REPLACED_BY_SUCCESS',

              retiredAt:
                now,
            },
          };

        await tx
          .objectStore(
            'restore_operations',
          )
          .put(
            completed,
            completed.restoreOperationId,
          );

        await tx
          .objectStore('control')
          .put({
            ...control,

            controlEpoch:
              control.controlEpoch + 1,

            recoveryDatasetId:
              null,

            restoreFence:
              null,
          });

        return {
          ok: true,
          value: {
            status:
              'COMPLETED',

            activeDatasetId:
              operation.stagingDatasetId,
          },
        };
      },
    );
  }

  private async finalizeRollback(
    operationId: string,
    now: string,
  ): Promise<
    RestoreResult<RestoreApplyOutcome>
  > {
    return this.transaction(
      'readwrite',
      async tx => {
        const control =
          await tx
            .objectStore('control')
            .get('control');

        const operation =
          await tx
            .objectStore(
              'restore_operations',
            )
            .get(operationId);

        if (
          !validDatasetControl(
            control,
          )
          || !validRestoreOperation(
            operation,
          )
          || operation.status
            !== 'VERIFYING_ROLLBACK'
          || operation.sourceDatasetId
            === undefined
          || control.restoreFence
            ?.operationId
            !== operationId
          || control.activeDatasetId
            !== operation.sourceDatasetId
          || control.recoveryDatasetId
            !== operation.stagingDatasetId
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        if (
          !Number.isSafeInteger(
            control.controlEpoch + 1,
          )
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        const rolledBack:
          RestoreOperation = {
            ...operation,

            status:
              'ROLLED_BACK',

            updatedAt:
              now,

            rolledBackAt:
              now,

            recoveredDatasetId:
              operation.sourceDatasetId,

            retiredCleanupCandidate: {
              datasetId:
                operation.stagingDatasetId,

              reason:
                'FAILED_TARGET_AFTER_ROLLBACK',

              retiredAt:
                now,
            },
          };

        await tx
          .objectStore(
            'restore_operations',
          )
          .put(
            rolledBack,
            rolledBack.restoreOperationId,
          );

        await tx
          .objectStore('control')
          .put({
            ...control,

            controlEpoch:
              control.controlEpoch + 1,

            recoveryDatasetId:
              null,

            restoreFence:
              null,
          });

        return {
          ok: true,
          value: {
            status:
              'ROLLED_BACK',

            activeDatasetId:
              operation.sourceDatasetId,
          },
        };
      },
    );
  }

  private async cleanupRetired(
    operationId: string,
  ): Promise<RestoreResult<void>> {
    return this.transaction(
      'readwrite',
      async tx => {
        const control =
          await tx
            .objectStore('control')
            .get('control');

        const operation =
          await tx
            .objectStore(
              'restore_operations',
            )
            .get(operationId);

        if (
          !validDatasetControl(
            control,
          )
          || !validRestoreOperation(
            operation,
          )
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        const candidate =
          operation
            .retiredCleanupCandidate;

        if (!candidate) {
          return {
            ok: true,
            value: undefined,
          };
        }

        if (
          !isTerminalRestoreStatus(
            operation.status,
          )
          || control.restoreFence
            !== null
          || control.recoveryDatasetId
            !== null
          || control.activeDatasetId
            === candidate.datasetId
        ) {
          return failure(
            'RESTORE_IN_PROGRESS',
          );
        }

        const operations =
          await tx
            .objectStore(
              'restore_operations',
            )
            .getAll();

        for (
          const other
          of operations
        ) {
          if (
            !validRestoreOperation(
              other,
            )
          ) {
            return failure(
              'RESTORE_CORRUPTION',
            );
          }

          if (
            other.restoreOperationId
              === operationId
            || isTerminalRestoreStatus(
              other.status,
            )
          ) {
            continue;
          }

          if (
            other.stagingDatasetId
              === candidate.datasetId
            || other.sourceDatasetId
              === candidate.datasetId
          ) {
            return failure(
              'RESTORE_IN_PROGRESS',
            );
          }
        }

        if (
          this.faults.hit(
            'cleanup-failure',
          )
        ) {
          return failure(
            'STORAGE_UNAVAILABLE',
          );
        }

        await this.deleteDataset(
          tx,
          candidate.datasetId,
        );

        return {
          ok: true,
          value: undefined,
        };
      },
    );
  }

  private async resume(
    operationId: string,
    now: string,
  ): Promise<
    RestoreResult<RestoreApplyOutcome>
  > {
    for (
      let iteration = 0;
      iteration < 12;
      iteration += 1
    ) {
      const current =
        await this.state(
          operationId,
        );

      if (!current.ok) {
        return current;
      }

      const {
        control,
        operation,
      } = current.value;

      if (
        operation.status
          === 'COMPLETED'
      ) {
        if (
          control.restoreFence
            !== null
          || control.recoveryDatasetId
            !== null
          || operation.successDatasetId
            === undefined
          || control.activeDatasetId
            !== operation.successDatasetId
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        await this.cleanupRetired(
          operationId,
        );

        return {
          ok: true,
          value: {
            status:
              'COMPLETED',

            activeDatasetId:
              operation.successDatasetId,
          },
        };
      }

      if (
        operation.status
          === 'ROLLED_BACK'
      ) {
        if (
          control.restoreFence
            !== null
          || control.recoveryDatasetId
            !== null
          || operation.recoveredDatasetId
            === undefined
          || control.activeDatasetId
            !== operation.recoveredDatasetId
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        await this.cleanupRetired(
          operationId,
        );

        return {
          ok: true,
          value: {
            status:
              'ROLLED_BACK',

            activeDatasetId:
              operation.recoveredDatasetId,
          },
        };
      }

      if (
        control.restoreFence
          ?.operationId
          !== operationId
      ) {
        return failure(
          'RESTORE_CORRUPTION',
        );
      }

      switch (
        operation.status
      ) {
        case 'APPLYING': {
          const switched =
            await this.forwardSwitch(
              operationId,
              now,
            );

          if (!switched.ok) {
            return switched;
          }

          if (
            this.faults.hit(
              'after-switch',
            )
          ) {
            return failure(
              'STORAGE_UNAVAILABLE',
            );
          }

          break;
        }

        case 'SWITCHING': {
          const marked =
            await this.markVerifying(
              operationId,
              now,
            );

          if (!marked.ok) {
            return marked;
          }

          break;
        }

        case 'VERIFYING': {
          const verified =
            await this.verifyDataset(
              operation.stagingDatasetId,
              operation,
            );

          if (!verified.ok) {
            return verified;
          }

          if (verified.value) {
            if (
              this.faults.hit(
                'before-finalize',
              )
            ) {
              return failure(
                'STORAGE_UNAVAILABLE',
              );
            }

            const completed =
              await this.finalizeSuccess(
                operationId,
                now,
              );

            if (!completed.ok) {
              return completed;
            }

            await this.cleanupRetired(
              operationId,
            );

            return completed;
          }

          const rollingBack =
            await this.markRollingBack(
              operationId,
              now,
            );

          if (!rollingBack.ok) {
            return rollingBack;
          }

          break;
        }

        case 'ROLLING_BACK': {
          const switchedBack =
            await this.switchBack(
              operationId,
              now,
            );

          if (!switchedBack.ok) {
            return switchedBack;
          }

          if (
            this.faults.hit(
              'after-rollback-switch',
            )
          ) {
            return failure(
              'STORAGE_UNAVAILABLE',
            );
          }

          break;
        }

        case 'VERIFYING_ROLLBACK': {
          if (
            operation.sourceDatasetId
              === undefined
          ) {
            return failure(
              'RESTORE_CORRUPTION',
            );
          }

          const verified =
            await this.verifyDataset(
              operation.sourceDatasetId,
              operation,
            );

          if (!verified.ok) {
            return verified;
          }

          if (!verified.value) {
            /*
             * Do NOT clear the fence:
             * neither generation has been proven safe.
             */
            return failure(
              'RESTORE_CORRUPTION',
            );
          }

          const rolledBack =
            await this.finalizeRollback(
              operationId,
              now,
            );

          if (!rolledBack.ok) {
            return rolledBack;
          }

          await this.cleanupRetired(
            operationId,
          );

          return rolledBack;
        }

        case 'BUILDING':
        case 'VALIDATED':
        case 'ABANDONED':
          return failure(
            'RESTORE_OPERATION_CONFLICT',
          );
      }
    }

    return failure(
      'RESTORE_CORRUPTION',
    );
  }

  async apply(
    preview: RestorePreviewToken,
    now: string,
  ): Promise<
    RestoreResult<RestoreApplyOutcome>
  > {
    if (
      !validRestorePreviewToken(
        preview,
      )
    ) {
      return failure(
        'RESTORE_OPERATION_CONFLICT',
      );
    }

    const initial =
      await this.state(
        preview.restoreOperationId,
      );

    if (!initial.ok) {
      return initial;
    }

    const {
      control,
      operation,
    } = initial.value;

    if (
      operation.preview
        === undefined
      || !samePreview(
        operation.preview,
        preview,
      )
    ) {
      return failure(
        'RESTORE_OPERATION_CONFLICT',
      );
    }

    if (
      operation.status
        === 'COMPLETED'
      || operation.status
        === 'ROLLED_BACK'
    ) {
      return this.resume(
        operation.restoreOperationId,
        now,
      );
    }

    if (
      control.restoreFence
        !== null
    ) {
      if (
        control.restoreFence
          .operationId
          !== operation
            .restoreOperationId
      ) {
        return failure(
          'RESTORE_IN_PROGRESS',
        );
      }

      return this.resume(
        operation.restoreOperationId,
        now,
      );
    }

    if (
      control.recoveryDatasetId
        !== null
    ) {
      return failure(
        'RESTORE_CORRUPTION',
      );
    }

    if (
      operation.status
        !== 'VALIDATED'
    ) {
      return failure(
        'RESTORE_OPERATION_CONFLICT',
      );
    }

    const fenced =
      await this.acquireFence(
        preview,
        now,
      );

    if (!fenced.ok) {
      return fenced;
    }

    if (
      this.faults.hit(
        'after-fence',
      )
    ) {
      return failure(
        'STORAGE_UNAVAILABLE',
      );
    }

    return this.resume(
      operation.restoreOperationId,
      now,
    );
  }

  async discard(
    preview: RestorePreviewToken,
    now: string,
  ): Promise<RestoreResult<void>> {
    if (
      !validRestorePreviewToken(
        preview,
      )
    ) {
      return failure(
        'RESTORE_OPERATION_CONFLICT',
      );
    }

    return this.transaction(
      'readwrite',
      async tx => {
        const control =
          await tx
            .objectStore('control')
            .get('control');

        const operation =
          await tx
            .objectStore(
              'restore_operations',
            )
            .get(
              preview.restoreOperationId,
            );

        if (
          !validDatasetControl(
            control,
          )
          || !validRestoreOperation(
            operation,
          )
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        if (
          control.restoreFence
            !== null
          || control.recoveryDatasetId
            !== null
        ) {
          return failure(
            'RESTORE_IN_PROGRESS',
          );
        }

        if (
          operation.status
            !== 'VALIDATED'
          || operation.preview
            === undefined
          || !samePreview(
            operation.preview,
            preview,
          )
          || operation.stagingDatasetId
            === control.activeDatasetId
        ) {
          return failure(
            'RESTORE_OPERATION_CONFLICT',
          );
        }

        await this.deleteDataset(
          tx,
          operation.stagingDatasetId,
        );

        const abandoned:
          RestoreOperation = {
            ...operation,

            status:
              'ABANDONED',

            updatedAt:
              now,

            abandonedAt:
              now,

            retiredCleanupCandidate: {
              datasetId:
                operation.stagingDatasetId,

              reason:
                'ABANDONED_STAGING',

              retiredAt:
                now,
            },
          };

        await tx
          .objectStore(
            'restore_operations',
          )
          .put(
            abandoned,
            abandoned.restoreOperationId,
          );

        return {
          ok: true,
          value: undefined,
        };
      },
    );
  }

  async pendingPreview(): Promise<
    RestoreResult<
      RestorePreviewToken | null
    >
  > {
    return this.transaction(
      'readonly',
      async tx => {
        const control =
          await tx
            .objectStore('control')
            .get('control');

        if (
          !validDatasetControl(
            control,
          )
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        if (
          control.restoreFence
            !== null
          || control.recoveryDatasetId
            !== null
        ) {
          return failure(
            'RESTORE_IN_PROGRESS',
          );
        }

        const operations =
          await tx
            .objectStore(
              'restore_operations',
            )
            .getAll();

        if (
          operations.some(
            operation =>
              !validRestoreOperation(
                operation,
              ),
          )
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        const nonterminal =
          operations.filter(
            operation =>
              !isTerminalRestoreStatus(
                operation.status,
              ),
          );

        if (
          nonterminal.length === 0
        ) {
          return {
            ok: true,
            value: null,
          };
        }

        if (
          nonterminal.length !== 1
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        const operation =
          nonterminal[0];

        if (!operation) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        if (
          operation.status
            !== 'VALIDATED'
          || operation.preview
            === undefined
          || operation.sourceDatasetId
            === undefined
          || operation.stagingDatasetId
            === control.activeDatasetId
          || operation.preview
            .restoreOperationId
            !== operation
              .restoreOperationId
          || operation.preview
            .stagingDatasetId
            !== operation
              .stagingDatasetId
          || operation.preview
            .activeDatasetId
            !== operation
              .sourceDatasetId
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        /*
         * Deliberately do NOT require
         * previewMatchesControl().
         *
         * A writer may have made this preview stale
         * after the original tab disappeared. The safe
         * recovery action is still to expose the exact
         * token so the user can explicitly DISCARD the
         * staging generation.
         */
        return {
          ok: true,
          value:
            operation.preview,
        };
      },
    );
  }

  async recover(
    now: string,
  ): Promise<
    RestoreResult<RestoreRecoveryOutcome>
  > {
    const inspected =
      await this.transaction<
        'readonly',
        {
          control:
            DatasetControl | null;

          operations:
            RestoreOperation[];
        }
      >(
        'readonly',
        async tx => {
          const control =
            await tx
              .objectStore('control')
              .get('control');

          /*
           * First launch: Restore opens the physical DB
           * before DailyRoutine creates the initial
           * DatasetControl.
           */
          if (!control) {
            return {
              ok: true,
              value: {
                control: null,
                operations: [],
              },
            };
          }

          if (
            !validDatasetControl(
              control,
            )
          ) {
            return failure(
              'RESTORE_CORRUPTION',
            );
          }

          const operations =
            await tx
              .objectStore(
                'restore_operations',
              )
              .getAll();

          if (
            operations.some(
              operation =>
                !validRestoreOperation(
                  operation,
                ),
            )
          ) {
            return failure(
              'RESTORE_CORRUPTION',
            );
          }

          return {
            ok: true,
            value: {
              control,
              operations,
            },
          };
        },
      );

    if (!inspected.ok) {
      return inspected;
    }

    if (
      inspected.value.control
        === null
    ) {
      return {
        ok: true,
        value: {
          status: 'IDLE',
        },
      };
    }

    const {
      control,
      operations,
    } = inspected.value;

    if (
      control.restoreFence
        === null
    ) {
      if (
        control.recoveryDatasetId
          !== null
      ) {
        return failure(
          'RESTORE_CORRUPTION',
        );
      }

      const nonterminal =
        operations.filter(
          operation =>
            !isTerminalRestoreStatus(
              operation.status,
            ),
        );

      const dangerous =
        nonterminal.filter(
          operation =>
            operation.status
              !== 'VALIDATED',
        );

      if (
        dangerous.length > 0
        || nonterminal.length > 1
      ) {
        return failure(
          'RESTORE_CORRUPTION',
        );
      }

      for (
        const operation
        of operations
      ) {
        if (
          isTerminalRestoreStatus(
            operation.status,
          )
        ) {
          await this.cleanupRetired(
            operation
              .restoreOperationId,
          );
        }
      }

      return {
        ok: true,
        value: {
          status:
            nonterminal.length === 1
              ? 'PREVIEW_PENDING'
              : 'IDLE',
        },
      };
    }

    const operation =
      operations.find(
        item =>
          item.restoreOperationId
            === control.restoreFence
              ?.operationId,
      );

    if (!operation) {
      return failure(
        'RESTORE_CORRUPTION',
      );
    }

    const resumed =
      await this.resume(
        operation.restoreOperationId,
        now,
      );

    if (!resumed.ok) {
      return resumed;
    }

    return {
      ok: true,
      value: {
        status:
          resumed.value.status
            === 'COMPLETED'
            ? 'RECOVERED_COMPLETED'
            : 'RECOVERED_ROLLED_BACK',
      },
    };
  }

  /*
   * Existing project infrastructure already contains
   * explicit Lab fault seams. These two methods are
   * observable test support only and are never exposed
   * through the public RestoreRepository port.
   */
  async inspectForLab() {
    return this.transaction(
      'readonly',
      async tx => {
        const control =
          await tx
            .objectStore('control')
            .get('control');

        const operations =
          await tx
            .objectStore(
              'restore_operations',
            )
            .getAll();

        if (
          control
          && !validDatasetControl(
            control,
          )
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        if (
          operations.some(
            operation =>
              !validRestoreOperation(
                operation,
              ),
          )
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        const datasetIds =
          new Set<string>();

        if (control) {
          datasetIds.add(
            control.activeDatasetId,
          );

          if (
            control.recoveryDatasetId
              !== null
          ) {
            datasetIds.add(
              control.recoveryDatasetId,
            );
          }
        }

        for (
          const operation
          of operations
        ) {
          datasetIds.add(
            operation.stagingDatasetId,
          );

          if (
            operation.sourceDatasetId
          ) {
            datasetIds.add(
              operation.sourceDatasetId,
            );
          }

          if (
            operation
              .retiredCleanupCandidate
          ) {
            datasetIds.add(
              operation
                .retiredCleanupCandidate
                .datasetId,
            );
          }
        }

        const datasetTotals:
          Record<string, number> = {};

        for (
          const datasetId
          of datasetIds
        ) {
          datasetTotals[datasetId] =
            await this.countDataset(
              tx,
              datasetId,
            );
        }

        return {
          ok: true,
          value: {
            control:
              control ?? null,

            operations,

            datasetTotals,
          },
        };
      },
    );
  }

  async corruptStagingForLab(
    datasetId: string,
  ): Promise<RestoreResult<void>> {
    return this.transaction(
      'readwrite',
      async tx => {
        const control =
          await tx
            .objectStore('control')
            .get('control');

        if (
          !validDatasetControl(
            control,
          )
          || datasetId
            === control.activeDatasetId
          || datasetId
            === control.recoveryDatasetId
        ) {
          return failure(
            'RESTORE_OPERATION_CONFLICT',
          );
        }

        const headCursor =
          await tx
            .objectStore('heads')
            .openCursor(
              scope(datasetId),
            );

        if (!headCursor) {
          return failure(
            'RESTORE_OPERATION_CONFLICT',
          );
        }

        const head =
          headCursor.value;

        if (
          head.datasetId !== datasetId
          || !head.check
        ) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        const historical =
          await tx
            .objectStore('history')
            .get([
              datasetId,
              head.check.checkId,
              head.check.revision,
            ]);

        if (!historical) {
          return failure(
            'RESTORE_CORRUPTION',
          );
        }

        await tx
          .objectStore('history')
          .delete([
            datasetId,
            head.check.checkId,
            head.check.revision,
          ]);

        return {
          ok: true,
          value: undefined,
        };
      },
    );
  }
}

export async function openRestoreRepository(
  databaseName: string,
  faults = new RestoreLabFaults(),
): Promise<
  RestoreResult<IndexedRestoreRepository>
> {
  const opened =
    await openFenixDatabase(
      databaseName,
    );

  if (!opened.ok) {
    return failure(
      openCode(
        opened.code,
      ),
    );
  }

  return {
    ok: true,
    value:
      new IndexedRestoreRepository(
        opened.value,
        faults,
      ),
  };
}